import React, { useMemo, useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Trans } from 'react-i18next';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { Header2SemiBold } from '@ubeya/shared-web/components';
import useArrayManagement from '@ubeya/shared-web/hooks/useArrayManagement';
import { ReactComponent as BaseBreakIcon } from '@ubeya/shared-web/assets/break.svg';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import SmallTable from './SmallTable';
import BreakModal from './BreakModal';
import ActionsRenderer from './ActionsRenderer';
import AddLink from './AddLink';

const Wrapper = styled.div.attrs({ className: 'break-table-wrapper' })`
  .small-table-wrapper {
    width: 100%;

    .row > div:first-child {
      padding-left: 5px;
    }
  }
`;

const BreakIcon = styled(BaseBreakIcon)`
  margin-right: 15px;
  margin-bottom: -5px;
`;

const BreaksTable = ({ branchId, date, startTime, endTime, showTitle = true, readOnly = false }) => {
  const [selectedBreak, setSelectedBreak] = useState(false);
  const { timeFormat } = useContext(ConfigContext);
  const { t } = useContext(ConfigContext);
  const { data, handleAdd, handleEdit, handleDelete } = useArrayManagement('breaktimes');
  const { mappedBranches } = useBranches();
  const { config } = useAccount();
  const isRulesEngineEnabled = config?.enableRulesEngineFeature;

  const mappedBreakTypes = useMemo(
    () =>
      (mappedBranches?.[branchId]?.timesheetConfig?.breakTypes || []).reduce((all, type) => {
        all[type.id] = type;
        return all;
      }, {}),
    [branchId, mappedBranches]
  );

  const getFormattedTime = useCallback((time) => moment(time).format(timeFormat), [timeFormat]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: t('hours'),
        dataKey: 'range',
        formatter: (_, columnData) =>
          `${getFormattedTime(columnData.startTime)} - ${
            columnData.endTime && moment(columnData.endTime).isValid()
              ? `${getFormattedTime(columnData.endTime)}`
              : t('onBreak')
          }`
      },
      {
        Header: t('breakType'),
        dataKey: 'breakTypeId',
        formatter: (breakTypeId) => (breakTypeId ? mappedBreakTypes?.[breakTypeId]?.title : '')
      }
    ];

    // Only include ruleName column if rules engine feature is enabled
    if (isRulesEngineEnabled) {
      baseColumns.push({
        Header: t('ruleName'),
        dataKey: 'complianceRuleId',
        formatter: (complianceRuleId, columnData) => {
          if (!complianceRuleId || !columnData.complianceRule) {
            return '-';
          }
          const ruleName = columnData.complianceRule.name;
          const ruleSetName = columnData.complianceRule.ruleSet?.name;
          return ruleSetName ? `${ruleName} (${ruleSetName})` : ruleName;
        }
      });
    }

    baseColumns.push({
      Header: t('comments'),
      dataKey: 'comments'
    });

    return baseColumns;
  }, [getFormattedTime, mappedBreakTypes, t, isRulesEngineEnabled]);

  return (
    <Wrapper>
      {showTitle && (
        <Header2SemiBold>
          <BreakIcon />
          <Trans>breaks</Trans>
        </Header2SemiBold>
      )}

      {data.length > 0 && (
        <SmallTable
          columns={columns}
          data={data}
          autoHeight
          headless={false}
          headerRowHeight={50}
          actionRenderer={({ rowData }) =>
            readOnly ? undefined : (
              <ActionsRenderer
                rowData={rowData}
                columnData={{
                  onEdit: (editedRowData) => setSelectedBreak(editedRowData),
                  onDelete: (deletedRowData) => handleDelete(deletedRowData.id)
                }}
              />
            )
          }
        />
      )}

      {startTime && endTime && !readOnly && (
        <AddLink onClick={() => setSelectedBreak({})}>
          <Trans>addBreak</Trans>
        </AddLink>
      )}

      {selectedBreak && (
        <BreakModal
          branchId={branchId}
          startTime={startTime}
          selectedBreak={selectedBreak}
          breaktimes={data}
          onConfirm={(values) => (selectedBreak.id ? handleEdit(selectedBreak.id, values) : handleAdd(values))}
          onClose={() => setSelectedBreak(undefined)}
          onDelete={() => handleDelete(selectedBreak.id)}
          date={date}
        />
      )}
    </Wrapper>
  );
};

export default BreaksTable;
